import React, {ChangeEvent, FC} from "react";
import {Autocomplete} from "@material-ui/lab";
import PlacesAutocomplete, {geocodeByAddress, Suggestion} from "react-places-autocomplete";
import TextField from "@material-ui/core/TextField";
import parse from "autosuggest-highlight/parse";
import Grid from "@material-ui/core/Grid";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {Collapse, List, ListItem, TextFieldProps} from "@material-ui/core";
import {Address} from "../redux/types";
import {useField} from "formik";

const useStyles = makeStyles((theme) => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2),
    },
    listHeader: {
        color: theme.palette.warning.main
    }
}));

interface DcbAddressInputProps {
    label?: string;
    onChange: (res: Address | null) => void;
    value?: Address | null;
    textFieldPops?: TextFieldProps;
}

function getGeoCoderResultAddressComponents(g: google.maps.GeocoderResult) {
    let street: string = "";
    let streetNumber: string = "";
    let postalCode: string = "";
    let city: string = "";

    for (const comp of g.address_components) {
        if (comp.types.includes("street_number")) {
            streetNumber = comp.long_name;
        } else if (comp.types.includes("route")) {
            street = comp.long_name;
        } else if (comp.types.includes("postal_code")) {
            postalCode = comp.long_name
        } else if (comp.types.includes("locality")) {
            city = comp.long_name
        }
    }
    return {street, streetNumber, postalCode, city}
}

function formatGeoCoderResult(g: google.maps.GeocoderResult) {
    const {street, streetNumber, postalCode, city} = getGeoCoderResultAddressComponents(g);
    return `${street} ${streetNumber}, ${postalCode} ${city}`;
}

export const DcbAddressInput: FC<DcbAddressInputProps> = (props) => {
    const classes = useStyles();
    const [searchString, setSearchString] = React.useState("");
    const [selectedPrediction, setSelectedPrediction] = React.useState<string>();
    const [geoCoderResults, setGeoCoderResults] = React.useState<google.maps.GeocoderResult[] | null>(null);

    const selectGeoCoderResult = (g: google.maps.GeocoderResult) => {
        props.onChange({
            placeId: g.place_id,
            formattedAddress: formatGeoCoderResult(g),
            lat: g.geometry.location.lat(),
            long: g.geometry.location.lng()
        });
        setGeoCoderResults(null);
    };

    return (
        <div>
            <PlacesAutocomplete
                key={props.value?.formattedAddress ?? '-none'}
                value={searchString}
                onChange={setSearchString}
                searchOptions={{
                    componentRestrictions: {
                        country: "de"
                    },
                    bounds: {
                        north: 52.781741,
                        east: 14.442019,
                        south: 52.022500,
                        west: 13.207600
                    }
                }}
            >
                {({suggestions, getInputProps}) =>
                    (<Autocomplete
                        filterOptions={(t) => t}
                        fullWidth
                        includeInputInList
                        value={props.value ? props.value.formattedAddress : selectedPrediction}
                        options={(suggestions as Suggestion[]).map(v => v.description)}
                        onChange={(e: ChangeEvent<{}>, newSelectedValue: string | null) => {
                            setSelectedPrediction(newSelectedValue ?? undefined);
                            if (newSelectedValue) {
                                geocodeByAddress(newSelectedValue).then(setGeoCoderResults);
                            }
                        }}
                        getOptionLabel={(option) => option}
                        renderInput={(params) =>
                            <TextField
                                {...params}
                                {...getInputProps()}
                                {...props.textFieldPops}
                                label={props.label}
                                fullWidth
                            />
                        }
                        renderOption={(optionPlain) => {
                            const option = suggestions.find(s => s.description === optionPlain) as Suggestion;
                            const matches = option.matchedSubstrings;
                            const parts = parse(
                                option.formattedSuggestion.mainText,
                                matches.map((match: any) => [match.offset, match.offset + match.length]),
                            );
                            return (
                                <Grid container alignItems="center">
                                    <Grid item>
                                        <LocationOnIcon className={classes.icon}/>
                                    </Grid>
                                    <Grid item xs>
                                        {parts.map((part: any, index: number) => (
                                                <span
                                                    key={index}
                                                    style={{fontWeight: part.highlight ? 700 : 400}}
                                                >
                                                    {part.text}
                                                </span>
                                            )
                                        )}
                                        <Typography variant="body2" color="textSecondary">
                                            {option.formattedSuggestion.secondaryText}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            );
                        }}
                    />)
                }
            </PlacesAutocomplete>
            <Collapse in={geoCoderResults !== null} style={{paddingTop: 8}}>
                <Typography variant="caption" color="textSecondary">
                    wählen Sie eine der Addressen aus
                </Typography>
                <Typography className={classes.listHeader}>
                    Meinten Sie:
                </Typography>
                <List>
                    {geoCoderResults?.map((g) => {
                            return (
                                <ListItem
                                    key={g.place_id}
                                    button
                                    onClick={() => selectGeoCoderResult(g)}
                                >
                                    {formatGeoCoderResult(g)}
                                </ListItem>
                            )
                        }
                    )}
                </List>
            </Collapse>
        </div>
    )
}

export const FormikDcbAddressInput: FC<{ label?: string, name: string; textFieldProps?: TextFieldProps }> = ({
                                                                                                                 label,
                                                                                                                 name,
                                                                                                                 textFieldProps
                                                                                                             }) => {
    const [, meta, helpers] = useField<Address | undefined>(name);

    return (
        <React.Fragment>
            <DcbAddressInput
                label={label}
                value={meta.value}
                onChange={(a) => helpers.setValue(a ?? undefined)}
                textFieldPops={textFieldProps}
            />
        </React.Fragment>
    )
}
